import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Chip,
  Button,
  MenuItem,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Paper,
  Tooltip,
  useTheme,
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BadgeIcon from '@mui/icons-material/Badge';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AbcIcon from '@mui/icons-material/Abc';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';

type Modo = 'duplicados' | 'todos';

const num = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');
const formatDateBr = (iso?: string | null): string =>
  iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : '-';

const CRITERIO_LABEL: Record<string, string> = {
  cns: 'CNS igual',
  cpf: 'CPF igual',
  nome: 'Nome igual',
};

const SITUACAO_LABEL: Record<string, string> = {
  ativa: 'Lotação ativa',
  encerrada: 'Lotação encerrada',
  sem_lotacao: 'Sem lotação',
};

const SITUACAO_COR: Record<string, 'success' | 'default' | 'warning'> = {
  ativa: 'success',
  encerrada: 'default',
  sem_lotacao: 'warning',
};

interface KpiProps {
  icon: React.ReactNode;
  label: string;
  valor: React.ReactNode;
  cor: string;
  hint?: string;
}

const Kpi: React.FC<KpiProps> = ({ icon, label, valor, cor, hint }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ textAlign: 'center' }}>
        <Box sx={{ color: cor }}>{icon}</Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
          {hint && (
            <Tooltip title={hint}>
              <InfoOutlinedIcon sx={{ fontSize: 13, ml: 0.4, verticalAlign: 'middle', opacity: 0.6 }} />
            </Tooltip>
          )}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ color: cor }}>
          {valor}
        </Typography>
      </CardContent>
    </Card>
  );
};

/**
 * Relatorio "Profissionais Duplicados".
 *
 * O cadastro de profissionais do sistema do municipio nao impede que a mesma pessoa exista em
 * dois registros. Quando isso acontece, a producao dela se divide entre os cadastros e a lotacao
 * fica pela metade. A tela agrupa por PESSOA (um registro entra no grupo por CNS, CPF ou nome) e
 * mostra lado a lado o que difere, para a gestao decidir o que unificar.
 *
 * Sem jargao tecnico na UI (nada de e-SUS, `tb_prof` ou CNES) — quem le e o gestor.
 */
const SaudeProfissionaisDuplicados: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [modo, setModo] = useState<Modo>('duplicados');
  const [situacao, setSituacao] = useState<string>('todas');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);

  const unidadeParam = unidadeSelecionada?.codunidade;

  // Debounce da busca: 400 ms e minimo de 3 caracteres (o backend ignora termos menores).
  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim().length >= 3 ? busca.trim() : ''), 400);
    return () => clearTimeout(t);
  }, [busca]);

  // Qualquer troca de filtro volta para a primeira pagina.
  useEffect(() => {
    setPage(0);
  }, [modo, situacao, buscaAplicada, unidadeParam]);

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    saudeService
      .getProfissionaisDuplicados(municipioAtivo.id, {
        modo,
        busca: buscaAplicada || undefined,
        situacao,
        unidade: unidadeParam,
        page: page + 1,
        limit,
      })
      .then((res: any) => setData(res))
      .catch((err: any) =>
        setError(err.response?.data?.message || 'Erro ao carregar o relatório de profissionais duplicados.'),
      )
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, modo, situacao, buscaAplicada, unidadeParam, page, limit]);

  const filtrosExport = useMemo(
    () => ({ modo, busca: buscaAplicada || undefined, situacao, unidade: unidadeParam }),
    [modo, buscaAplicada, situacao, unidadeParam],
  );

  const exportarCsv = useCallback(() => {
    if (!municipioAtivo?.id) return;
    saudeService
      .exportarProfissionaisDuplicadosCsv(municipioAtivo.id, filtrosExport)
      .catch(() => setError('Falha ao exportar CSV.'));
  }, [municipioAtivo?.id, filtrosExport]);

  const exportarPdf = useCallback(() => {
    if (!municipioAtivo?.id) return;
    saudeService
      .gerarProfissionaisDuplicadosPdf(municipioAtivo.id, filtrosExport)
      .catch(() => setError('Falha ao exportar PDF.'));
  }, [municipioAtivo?.id, filtrosExport]);

  if (!municipioAtivo?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Selecione um município para acessar o relatório.</Alert>
      </Box>
    );
  }

  const grupos: any[] = data?.grupos || [];
  const registros: any[] = data?.registros || [];
  const resumo = data?.resumo || {};
  const limitacoes: string[] = data?.limitacoes || [];
  const situacoes: any[] = data?.situacoes_disponiveis || [];
  const disponivel = !!data && data.dwh_implementado !== false && !data.consulta_indisponivel;
  const registrosExibidos = modo === 'duplicados' ? grupos.reduce((s, g) => s + g.registros.length, 0) : registros.length;
  const total = modo === 'todos' ? Number(data?.paginacao?.total || 0) : registrosExibidos;

  /** Uma linha de profissional — o mesmo desenho nos dois modos. */
  const linhaRegistro = (r: any, mostrarChips: boolean) => (
    <TableRow key={r.codigo} hover>
      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{r.codigo}</TableCell>
      <TableCell sx={{ fontWeight: 600 }}>
        {r.nome}
        {mostrarChips && (r.dup_cns || r.dup_cpf || r.dup_nome) && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            {r.dup_cns && <Chip label={CRITERIO_LABEL.cns} size="small" color="error" variant="outlined" />}
            {r.dup_cpf && <Chip label={CRITERIO_LABEL.cpf} size="small" color="error" variant="outlined" />}
            {r.dup_nome && <Chip label={CRITERIO_LABEL.nome} size="small" color="warning" variant="outlined" />}
          </Box>
        )}
        {r.cbos && (
          <Typography variant="caption" color="text.secondary" display="block">
            {r.cbos}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>{r.cns || '-'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>{r.cpf || '-'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateBr(r.dt_nascimento)}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.conselho || '-'}</TableCell>
      <TableCell>
        <Chip
          label={SITUACAO_LABEL[r.situacao] || r.situacao}
          size="small"
          color={SITUACAO_COR[r.situacao] || 'default'}
          variant={r.situacao === 'ativa' ? 'filled' : 'outlined'}
        />
      </TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>
        {r.unidades || '-'}
        {r.equipes && (
          <Typography variant="caption" color="text.secondary" display="block">
            {r.equipes}
          </Typography>
        )}
      </TableCell>
      <TableCell align="center">
        {r.tem_producao ? (
          <Tooltip title="Já há atendimento registrado com este CNS">
            <CheckCircleIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );

  const cabecalhoTabela = (
    <TableHead>
      <TableRow>
        <TableCell>Código</TableCell>
        <TableCell>Nome</TableCell>
        <TableCell>CNS</TableCell>
        <TableCell>CPF</TableCell>
        <TableCell>Nascimento</TableCell>
        <TableCell>Conselho</TableCell>
        <TableCell>Situação</TableCell>
        <TableCell>Unidade / equipe</TableCell>
        <TableCell align="center">Produção</TableCell>
      </TableRow>
    </TableHead>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            <PersonSearchIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#6a1b9a' }} />
            Profissionais Duplicados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cadastros de profissionais que podem representar a mesma pessoa
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={exportarCsv}
            disabled={loading || !disponivel || registrosExibidos === 0}
          >
            CSV
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportarPdf}
            disabled={loading || !disponivel}
          >
            PDF
          </Button>
        </Box>
      </Box>

      {/* Escopo e filtros */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={modo}
          onChange={(_e, v) => v && setModo(v)}
          disabled={loading}
        >
          <ToggleButton value="duplicados">Somente duplicados</ToggleButton>
          <ToggleButton value="todos">Todos</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          label="Pesquisar"
          placeholder="Nome, CNS ou CPF"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Situação"
          value={situacao}
          onChange={(e) => setSituacao(e.target.value)}
          disabled={loading}
          sx={{ minWidth: 200 }}
        >
          {(situacoes.length > 0
            ? situacoes
            : [{ slug: 'todas', label: 'Todas' }]
          ).map((s: any) => (
            <MenuItem key={s.slug} value={s.slug}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <Chip
          icon={<LocalHospitalIcon sx={{ fontSize: 16 }} />}
          label={
            unidadeSelecionada
              ? `${unidadeSelecionada.nome} — grupos com ao menos um vínculo na unidade`
              : 'Todas as unidades do município'
          }
          variant="outlined"
          color={unidadeSelecionada ? 'default' : 'primary'}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : data?.dwh_implementado === false ? (
        <Alert severity="info">
          <AlertTitle>Não disponível para este município</AlertTitle>
          {data.motivo ||
            'Este município lê os indicadores do Data Warehouse. O relatório de profissionais duplicados está implementado apenas para conexão direta ao sistema do município.'}
        </Alert>
      ) : data?.consulta_indisponivel ? (
        <Alert severity="warning">
          <AlertTitle>Não foi possível consultar o cadastro</AlertTitle>
          {data.motivo ||
            'Não foi possível consultar o cadastro de profissionais deste município.'}
        </Alert>
      ) : data ? (
        <>
          {/* KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Kpi
                icon={<BadgeIcon />}
                label="Profissionais cadastrados"
                valor={num(resumo.profissionais)}
                cor="#1565c0"
                hint="Total de registros no cadastro de profissionais do município"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Kpi
                icon={<ContentCopyIcon />}
                label="Grupos duplicados"
                valor={num(resumo.grupos)}
                cor={resumo.grupos > 0 ? '#c62828' : '#2e7d32'}
                hint="Conjuntos de cadastros que parecem ser a mesma pessoa"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Kpi
                icon={<PersonSearchIcon />}
                label="Registros envolvidos"
                valor={num(resumo.registros_duplicados)}
                cor={resumo.registros_duplicados > 0 ? '#e65100' : '#2e7d32'}
                hint="Cadastros que caem em pelo menos um grupo de duplicidade"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Kpi icon={<CreditCardIcon />} label="Por CNS" valor={num(resumo.por_cns)} cor="#6a1b9a" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Kpi icon={<FingerprintIcon />} label="Por CPF" valor={num(resumo.por_cpf)} cor="#6a1b9a" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Kpi icon={<AbcIcon />} label="Por nome" valor={num(resumo.por_nome)} cor="#6a1b9a" />
            </Grid>
          </Grid>

          {registrosExibidos === 0 ? (
            <Alert severity={modo === 'duplicados' && !buscaAplicada && situacao === 'todas' ? 'success' : 'info'} sx={{ mb: 3 }}>
              <AlertTitle>
                {modo === 'duplicados' ? 'Nenhuma duplicidade encontrada' : 'Nenhum profissional encontrado'}
              </AlertTitle>
              {modo === 'duplicados'
                ? 'Nenhum cadastro de profissional repete CNS, CPF ou nome com os filtros aplicados.'
                : 'Nenhum profissional atende aos filtros aplicados.'}
            </Alert>
          ) : (
            <Paper
              elevation={0}
              sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3, overflow: 'hidden' }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {modo === 'duplicados'
                    ? `${num(grupos.length)} grupo(s) — ${num(registrosExibidos)} registro(s)`
                    : `${num(total)} profissional(is)`}
                </Typography>
              </Box>
              <TableContainer sx={{ overflowX: 'auto', maxHeight: 640 }}>
                <Table size="small" stickyHeader>
                  {cabecalhoTabela}
                  {modo === 'duplicados' ? (
                    grupos.map((g: any) => (
                      <TableBody key={g.grupo}>
                        <TableRow>
                          <TableCell
                            colSpan={9}
                            sx={{
                              backgroundColor: theme.palette.action.hover,
                              py: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle2" fontWeight={700}>
                                Grupo {g.grupo}
                              </Typography>
                              {(g.criterios || []).map((c: string) => (
                                <Chip
                                  key={c}
                                  label={CRITERIO_LABEL[c] || c}
                                  size="small"
                                  color={c === 'nome' ? 'warning' : 'error'}
                                />
                              ))}
                              <Typography variant="caption" color="text.secondary">
                                {g.registros.length} registros
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {g.registros.map((r: any) => linhaRegistro(r, false))}
                      </TableBody>
                    ))
                  ) : (
                    <TableBody>{registros.map((r: any) => linhaRegistro(r, true))}</TableBody>
                  )}
                </Table>
              </TableContainer>
              {modo === 'todos' && (
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_e, p) => setPage(p)}
                  rowsPerPage={limit}
                  onRowsPerPageChange={(e) => {
                    setLimit(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100, 200]}
                  labelRowsPerPage="Linhas por página"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${num(count)}`}
                />
              )}
            </Paper>
          )}

          {limitacoes.length > 0 && (
            <Alert severity="info" icon={<InfoOutlinedIcon />}>
              <AlertTitle>Como interpretar</AlertTitle>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {limitacoes.map((l, i) => (
                  <li key={i}>
                    <Typography variant="body2">{l}</Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}
        </>
      ) : null}
    </Box>
  );
};

export default SaudeProfissionaisDuplicados;
